import type React from "react";
import { useState, createContext, useContext } from "react";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import type { components } from "@/lib/openapi";
import { getFilterConfig } from "@/queries/landing";
import { useDateContext } from "@/stores/DateContext";

type FlightFeeFilter = components["schemas"]["FlightFeeFilter"];
type FeeTier = components["schemas"]["FeeTier"];
type FilterConfig = components["schemas"]["FilterConfig"];

interface FilterContextType {
  filters: FlightFeeFilter;
  setFilters: React.Dispatch<React.SetStateAction<FlightFeeFilter>>;
  appliedFilters: FlightFeeFilter;
  setAppliedFilters: React.Dispatch<React.SetStateAction<FlightFeeFilter>>;
  filterConfig: FilterConfig | undefined;
  resetFilters: () => void;
}

const FilterContext = createContext<FilterContextType | undefined>(undefined);

export const useFilterContext = () => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error("useFilterContext must be used within a FilterProvider");
  }
  return context;
};

const defaultFilters: FlightFeeFilter = {
  carriers: null,
  flight_types: null,
  tiers: null,
  include_diversion: true,
};

export const FilterProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [filters, setFilters] = useState<FlightFeeFilter>(defaultFilters);
  const [appliedFilters, setAppliedFilters] =
    useState<FlightFeeFilter>(defaultFilters);

  const resetFilters = () => {
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
  };

  const contextValue: FilterContextType = {
    filters,
    setFilters,
    appliedFilters,
    setAppliedFilters,
    filterConfig: undefined,
    resetFilters,
  };

  return (
    <FilterContext.Provider value={contextValue}>
      {children}
    </FilterContext.Provider>
  );
};

interface Option {
  value: string;
  label: string;
}

interface MultiSelectProps {
  label: string;
  options: Option[];
  value: string[] | null;
  onChange: (value: string[] | null) => void;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  label,
  options,
  value,
  onChange,
}) => {
  const allSelected = value === null;
  const selectedCount = allSelected ? options.length : value?.length || 0;

  const handleSelectAll = (checked: boolean) => {
    onChange(checked ? null : []);
  };

  const handleChange = (optionValue: string, checked: boolean) => {
    if (allSelected) {
      // When deselecting one item while all were selected,
      // select all except this one
      onChange(options.map((o) => o.value).filter((v) => v !== optionValue));
    } else {
      onChange(
        checked
          ? [...(value || []), optionValue]
          : (value || []).filter((v) => v !== optionValue)
      );
    }
  };

  return (
    <div className="space-y-3 p-4 rounded-lg border border-slate-800 bg-slate-950/50">
      <div className="flex items-center justify-between">
        <Label className="font-semibold text-sm dark:text-slate-200">
          {label}
          <span className="ml-2 text-xs text-slate-500">
            ({selectedCount}/{options.length})
          </span>
        </Label>
        <div className="flex items-center space-x-2">
          <Checkbox
            id={`${label}-select-all`}
            checked={allSelected}
            onCheckedChange={handleSelectAll}
          />
          <Label
            htmlFor={`${label}-select-all`}
            className="text-sm text-slate-400"
          >
            Select All
          </Label>
        </div>
      </div>

      <div className="relative">
        <ScrollArea className="h-[200px] w-[calc(100%+8px)] -mr-2">
          <div className="pr-2 space-y-2">
            {options.map(({ value: optionValue, label: optionLabel }) => (
              <div
                key={optionValue}
                className="flex items-center space-x-2 py-1 px-1 hover:bg-slate-900/50 rounded"
              >
                <Checkbox
                  id={`${label}-${optionValue}`}
                  checked={allSelected || value?.includes(optionValue)}
                  onCheckedChange={(checked) =>
                    handleChange(optionValue, checked as boolean)
                  }
                />
                <Label
                  htmlFor={`${label}-${optionValue}`}
                  className="text-sm text-slate-300 cursor-pointer truncate max-w-full sm:max-w-[200px]"
                >
                  {optionLabel}
                </Label>
              </div>
            ))}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};

const FlightTypeFilter: React.FC = () => {
  const { filters, setFilters, filterConfig } = useFilterContext();

  const options = filterConfig?.flight_types
    ? Object.entries(filterConfig.flight_types).map(([value, label]) => ({
        value,
        label: label as string,
      }))
    : [];

  const handleChange = (value: string[] | null) => {
    setFilters((prev) => ({ ...prev, flight_types: value }));
  };

  return (
    <MultiSelect
      label="FLIGHT TYPES"
      options={options}
      value={filters.flight_types}
      onChange={handleChange}
    />
  );
};

const feeTiers: { value: FeeTier; label: string }[] = [
  { value: "signatory", label: "Signatory" },
  { value: "non_signatory", label: "Non-Signatory" },
  { value: "general_aviation", label: "General Aviation" },
];

const FeeTierFilter: React.FC = () => {
  const { filters, setFilters } = useFilterContext();

  const handleChange = (value: string[] | null) => {
    setFilters((prev) => ({ ...prev, tiers: value as FeeTier[] | null }));
  };

  return (
    <MultiSelect
      label="FEE TIERS"
      options={feeTiers}
      value={filters.tiers}
      onChange={handleChange}
    />
  );
};

const DiversionFilter: React.FC = () => {
  const { filters, setFilters } = useFilterContext();

  const handleDiversionChange = (checked: boolean) => {
    setFilters((prev) => ({
      ...prev,
      include_diversion: checked,
    }));
  };

  return (
    <div className="flex items-center space-x-2">
      <Switch
        id="include-diversion"
        className="opacity-70"
        checked={filters.include_diversion}
        onCheckedChange={handleDiversionChange}
      />
      <Label htmlFor="include-diversion" className="uppercase text-slate-400">
        Include Diversions
      </Label>
    </div>
  );
};

const CarrierFilter: React.FC = () => {
  const { filters, setFilters, filterConfig } = useFilterContext();

  const options = filterConfig?.carriers
    ? Object.entries(filterConfig.carriers).map(([code, label]) => ({
        value: code,
        label: label as string,
      }))
    : [];

  const handleChange = (value: string[] | null) => {
    setFilters((prev) => ({ ...prev, carriers: value }));
  };

  return (
    <MultiSelect
      label="CARRIERS"
      options={options}
      value={filters.carriers}
      onChange={handleChange}
    />
  );
};

export const FilterBuilder: React.FC = () => {
  const { filters, setFilters, appliedFilters, setAppliedFilters } =
    useFilterContext();
  const { startDate, endDate, singleDate } = useDateContext();

  const { data: filterConfig } = getFilterConfig({
    startDate: singleDate || startDate,
    endDate: singleDate ? undefined : endDate,
  });

  const handleApply = () => {
    setAppliedFilters(filters);
  };

  const contextValue: FilterContextType = {
    filters,
    setFilters,
    appliedFilters,
    setAppliedFilters,
    filterConfig,
    resetFilters: () => {
      setFilters(defaultFilters);
      setAppliedFilters(defaultFilters);
    },
  };

  return (
    <FilterContext.Provider value={contextValue}>
      <div className="w-full max-w-md mx-auto mt-4">
        <div className="space-y-3">
          <CarrierFilter />
          <FlightTypeFilter />
          <FeeTierFilter />
          <div className="p-4 rounded-lg border border-slate-800 bg-slate-950/50">
            <DiversionFilter />
          </div>
          <div className="pt-2">
            <Button
              onClick={handleApply}
              size="sm"
              variant="secondary"
              className="w-full"
            >
              Apply Filters
            </Button>
          </div>
        </div>
      </div>
    </FilterContext.Provider>
  );
};
